import type { Delivery, DeliveryDetail } from '@/services/db/delivery/entity';
import {
  deliveryStateValue,
  getDeliveryState,
  getDisplayState,
  isDeliveryMetricTimestamp,
  isMetricSourcedState,
} from '@/services/db/delivery/utils';
import { capitalize } from '@/utils/string';
import { channelValueOf, getDeliveryChannel } from '../catalog/channels';
import { channelFormatterType, formatError } from '@/utils/format-error';
import { metaValue } from './delivery-row-state';

/** One label + timestamp entry in the expanded row's timestamp stack. */
export interface TimestampRow {
  key: string;
  label: string;
  timestamp: number;
}

/** Delivery-state labels that diverge from a plain capitalization. */
const DELIVERY_STATE_LABELS: Record<string, string> = {
  attempted: 'First attempted',
  deferred: 'Deferred for later sending',
};

/** User-action labels that diverge from a plain capitalization. */
const POST_DELIVERY_LABELS: Record<string, string> = {
  spammed: 'Reported spam',
  human_opened: 'Human opened',
  human_clicked: 'Human clicked',
  prefetch_opened: 'Machine opened',
  machine_clicked: 'Machine clicked',
};

function channelStates(delivery: Delivery): { deliveryStates: readonly string[]; deliveryActions: readonly string[] } {
  const channel = getDeliveryChannel(channelValueOf(delivery.action_type));
  return { deliveryStates: channel?.deliveryStates ?? [], deliveryActions: channel?.deliveryActions ?? [] };
}

function toRows(delivery: Delivery, states: readonly string[], label: (state: string) => string): TimestampRow[] {
  return states
    .map((state) => ({ key: state, label: label(state), timestamp: deliveryStateValue(delivery, state) }))
    .filter((row) => row.timestamp > 0);
}

/**
 * The delivery-lifecycle timestamps (attempted/sent/deferred/…), each shown as
 * a duration from `created`. `holdout` is dropped and `canceled` appended,
 * matching the model's `deliveryTimestamps`.
 */
export function deliveryTimestampRows(delivery: Delivery): TimestampRow[] {
  const states = [...channelStates(delivery).deliveryStates.filter((state) => state !== 'holdout'), 'canceled'];
  return toRows(delivery, states, (state) => DELIVERY_STATE_LABELS[state] ?? capitalize(state));
}

/**
 * The engagement-breakdown keys Ember's `actionInfo()` prepends to **email**'s
 * `deliveryActions` (they aren't in the static channel catalog). Without them the
 * expansion never renders "Human opened" / "Machine clicked" — and the `opened`/`clicked`
 * filtering below, which exists to prioritize the human split, has nothing to prioritize.
 */
const EMAIL_ENGAGEMENT_ACTIONS = ['human_opened', 'human_clicked', 'prefetch_opened', 'machine_clicked'];

/**
 * Whether an engagement key holds a real timestamp for this delivery. The four email
 * breakdown keys are read from the opaque `metrics` map, which mixes counts and
 * timestamps, so they go through the bounds check — a count of `2` must read as absent
 * rather than render at 1 Jan 1970. Every other key is a top-level timestamp attr.
 */
function hasEngagementTimestamp(delivery: Delivery, state: string): boolean {
  const value = deliveryStateValue(delivery, state);
  return isMetricSourcedState(state) ? isDeliveryMetricTimestamp(value, delivery.created) : value > 0;
}

/**
 * The user-engagement timestamps (opened/clicked/converted/…), shown as absolute times.
 * Ember parity (`expansion` `postDeliveryTimestamps`): email gains the human/machine
 * breakdown keys, and the aggregate `opened`/`clicked` is dropped once that breakdown
 * carries a real timestamp, to prioritize the human split.
 */
export function postDeliveryActionRows(delivery: Delivery): TimestampRow[] {
  const isEmail = channelValueOf(delivery.action_type) === 'email';
  let actions = [...(isEmail ? EMAIL_ENGAGEMENT_ACTIONS : []), ...channelStates(delivery).deliveryActions];
  if (hasEngagementTimestamp(delivery, 'human_opened') || hasEngagementTimestamp(delivery, 'prefetch_opened')) {
    actions = actions.filter((action) => action !== 'opened');
  }
  if (hasEngagementTimestamp(delivery, 'human_clicked') || hasEngagementTimestamp(delivery, 'machine_clicked')) {
    actions = actions.filter((action) => action !== 'clicked');
  }
  return toRows(
    delivery,
    actions.filter((action) => hasEngagementTimestamp(delivery, action)),
    (state) => POST_DELIVERY_LABELS[state] ?? capitalize(state),
  );
}

/** States whose `meta.reason` surfaces as an informational reason line. */
const REASON_STATES = new Set(['failed', 'bounced', 'suppressed', 'attempted', 'undeliverable']);

/**
 * The reason region of the timestamp stack — mutually exclusive with the
 * user-action rows:
 * - `errors`: a failed delivery with error detail (from the fetched detail's
 *   `error_messages`, else the delivery's own error message).
 * - `reason`: a non-failed terminal state exposing a `meta.reason`.
 * - `none`: neither — the caller falls back to the user-action rows.
 */
export type ReasonView =
  | { kind: 'errors'; messages: string[] }
  | { kind: 'reason'; message: string }
  | { kind: 'none' };

function deliveryErrorMessage(delivery: Delivery): string | undefined {
  const failure = delivery.failure_message;
  if (failure != null && failure !== '') {
    return failure;
  }
  return metaValue(delivery, 'reason') ?? metaValue(delivery, 'failed_message');
}

/** Push deliveries encode per-device statuses as JSON in `meta.reason`. */
function hasDeviceStatuses(delivery: Delivery): boolean {
  if (delivery.action_type !== 'push_action') {
    return false;
  }
  const reason = metaValue(delivery, 'reason');
  if (reason == null) {
    return false;
  }
  try {
    const parsed: unknown = JSON.parse(reason);
    return parsed != null && typeof parsed === 'object';
  } catch {
    return false;
  }
}

export function deliveryReason(delivery: Delivery, detail: DeliveryDetail | undefined): ReasonView {
  const detailErrors = detail?.error_messages ?? [];
  const errorMessage = deliveryErrorMessage(delivery);
  const hasError = errorMessage != null || detailErrors.length > 0;

  // Channel + delivery state drive the message map (push codes, twilio codes);
  // unknown channels fall back to the raw string.
  const type = channelFormatterType(delivery.action_type);
  const deliveryState = getDeliveryState(delivery) ?? null;

  if (deliveryState === 'failed' && hasError && !hasDeviceStatuses(delivery)) {
    const raw = detailErrors.length > 0 ? detailErrors : errorMessage != null ? [errorMessage] : [];
    return { kind: 'errors', messages: raw.map((message) => formatError(message, { type, deliveryState })) };
  }

  const displayState = getDisplayState(delivery);
  const reason = metaValue(delivery, 'reason');
  if (displayState != null && REASON_STATES.has(displayState) && reason != null) {
    return { kind: 'reason', message: formatError(reason, { type, deliveryState }) };
  }

  return { kind: 'none' };
}
